import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Product {
  id: string;
  title: string;
  handle: string;
  onlineStoreUrl: string;
  featuredImage: {
    url: string;
    altText: string | null;
  };
  images?: {
    edges: Array<{
      node: {
        url: string;
        altText: string | null;
      }
    }>
  };
  price: {
    amount: string;
    currencyCode: string;
  };
}

interface ShopifyResponse {
  id: string;
  title: string;
  description: string;
  products: Product[];
}

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

export default function ShopifyMerch() {
  const [merchandise, setMerchandise] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hoveredProduct, setHoveredProduct] = useState<string | null>(null);

  useEffect(() => {
    const fetchMerchandise = async () => {
      try {
        const response = await fetch('https://k7qrhm4fsya6qyn4j3pezcabry0ljthf.lambda-url.eu-west-2.on.aws/');
        if (!response.ok) {
          throw new Error('Failed to fetch merchandise');
        }
        const data: ShopifyResponse = await response.json();
        setMerchandise(data.products);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchMerchandise();
  }, []);

  // Function to get the second image URL if available
  const getSecondImageUrl = (product: Product): string | null => {
    if (product.images && product.images.edges.length > 1) {
      return product.images.edges[1].node.url;
    }
    return null;
  };

  if (loading) return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="px-4 py-6"
    >
      Loading merchandise...
    </motion.div>
  );
  
  if (error) return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="px-4 py-6 text-red-500"
    >
      Error: {error}
    </motion.div>
  );

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="px-4 py-6"
    >
      <motion.div 
        layout
        className="flex justify-between items-center mb-3"
      >
        <motion.h2 
          layout
          className="text-lg font-semibold"
        >
          Merchandise
        </motion.h2>
        <motion.button 
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-sm text-gray-400 hover:text-white transition-all duration-200 flex items-center px-3 py-1.5 rounded-md hover:bg-zinc-800/50"
        >
          {isExpanded ? (
            <>
              Sluiten
              <X size={16} />
            </>
          ) : (
            `Toon alles (${merchandise.length})`
          )}
        </motion.button>
      </motion.div>
      
      <motion.div 
        layout
        variants={container}
        initial="hidden"
        animate="show"
        className={`
          ${isExpanded 
            ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4' 
            : 'flex gap-4 overflow-x-auto pb-4 snap-x'
          }
        `}
      >
        {merchandise.map((product, index) => {
          const secondImageUrl = getSecondImageUrl(product);
          
          return (
            <motion.a
              key={product.id}
              href={product.onlineStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              variants={item}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onHoverStart={() => setHoveredProduct(product.id)}
              onHoverEnd={() => setHoveredProduct(null)}
              className={`
                ${isExpanded ? 'w-full' : 'min-w-[200px]'}
                bg-zinc-900 rounded-lg overflow-hidden snap-start p-1 my-2
              `}
            >
              <div className="relative aspect-[3/4] rounded-lg overflow-hidden"
                style={{ minHeight: '275px' }}
              >
                <motion.div 
                  layout
                  className="absolute inset-0 bg-cover bg-center" 
                  style={{ backgroundImage: `url(${product.featuredImage.url})` }}
                  initial={{ opacity: 1 }}
                  animate={{ opacity: hoveredProduct === product.id && secondImageUrl ? 0 : 1 }}
                  transition={{ duration: 0.3 }}
                />
                {secondImageUrl && (
                  <motion.div 
                    layout
                    className="absolute inset-0 bg-cover bg-center" 
                    style={{ backgroundImage: `url(${secondImageUrl})` }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: hoveredProduct === product.id ? 1 : 0 }}
                    transition={{ duration: 0.3 }}
                  />
                )}
              </div>
              <motion.div 
                layout
                className="p-4"
              >
                <motion.h3 
                  layout
                  className="font-medium mb-1"
                >
                  {product.title}
                </motion.h3>
                <motion.p 
                  layout
                  className="text-sm text-gray-400"
                >
                  €{parseFloat(product.price.amount).toFixed(2)}
                </motion.p>
              </motion.div>
            </motion.a>
          );
        })}
      </motion.div>
    </motion.div>
  );
}
